/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { WorkspaceMember } from '@futrli-sdk/people';

import { Recurrence } from './useRecurrenceState';

const MAX_NUMBER_OF_USERS = 30;

export const FIELD_IDS = {
  SEND_DATE: 'schedule-report-send-date',
  REPEAT: 'schedule-report-repeat',
  EVERY: 'schedule-report-every',
  REVIEW: 'schedule-report-review',
  SEND_TO: 'schedule-report-send-to',
  SUBJECT: 'schedule-report-subject',
  BODY: 'schedule-report-body'
};

interface UseScheduleValidationParams {
  recurrence: Recurrence;
  recurrenceStartDateError: boolean;
  recurrenceRepeatValue: string;
  selectedUsers: WorkspaceMember[];
  emailSubject: string;
  emailBody: string;
}

/**
 * Provides form validation for the schedule report form.
 * Validates recurrence settings (start date, repeat interval) and email fields (recipients, subject, body).
 * Exposes `validate()` for on-submit checks and `revalidate()` for on-blur re-checks after initial submission.
 */
export const useScheduleValidation = ({
  recurrence,
  recurrenceStartDateError,
  recurrenceRepeatValue,
  selectedUsers,
  emailSubject,
  emailBody
}: UseScheduleValidationParams) => {
  const intl = useIntl();
  const [validationErrors, setValidationErrors] = useState<Record<string, { id: string; message: string }>>({});
  const hasBeenValidated = useRef(false);

  const validate = useCallback(() => {
    const errors: Record<string, { id: string; message: string }> = {};

    // Send date validation (only when recurrence is set)
    if (recurrence !== Recurrence.DOES_NOT_REPEAT && recurrenceStartDateError) {
      errors[FIELD_IDS.SEND_DATE] = {
        id: FIELD_IDS.SEND_DATE,
        message: intl.formatMessage({
          id: 'predict.schedule_start_date_in_past',
          defaultMessage: 'Start date must be today or a future date'
        })
      };
    }

    // Repeat validation
    if (!recurrence) {
      errors[FIELD_IDS.REPEAT] = {
        id: FIELD_IDS.REPEAT,
        message: intl.formatMessage({
          id: 'predict.schedule_repeat_required',
          defaultMessage: 'Repeat frequency is required'
        })
      };
    }

    // Every (repeat value) validation
    if (recurrence !== Recurrence.DOES_NOT_REPEAT && (!recurrenceRepeatValue || Number(recurrenceRepeatValue) < 1)) {
      errors[FIELD_IDS.EVERY] = {
        id: FIELD_IDS.EVERY,
        message: intl.formatMessage({
          id: 'predict.schedule_every_required',
          defaultMessage: 'Repeat interval must be at least 1'
        })
      };
    }

    // Send to validation
    if (selectedUsers.length === 0) {
      errors[FIELD_IDS.SEND_TO] = {
        id: FIELD_IDS.SEND_TO,
        message: intl.formatMessage({
          id: 'predict.email_pdf_select_recipients',
          defaultMessage: 'Please select at least one recipient'
        })
      };
    } else if (selectedUsers.length > MAX_NUMBER_OF_USERS) {
      errors[FIELD_IDS.SEND_TO] = {
        id: FIELD_IDS.SEND_TO,
        message: intl.formatMessage(
          { id: 'predict.email_pdf_max_recipients', defaultMessage: 'Maximum {max} recipients allowed' },
          { max: MAX_NUMBER_OF_USERS }
        )
      };
    }

    // Email subject validation
    if (!emailSubject.trim()) {
      errors[FIELD_IDS.SUBJECT] = {
        id: FIELD_IDS.SUBJECT,
        message: intl.formatMessage({
          id: 'predict.email_pdf_subject_required',
          defaultMessage: 'Email subject is required'
        })
      };
    }

    // Email body validation
    if (!emailBody.trim()) {
      errors[FIELD_IDS.BODY] = {
        id: FIELD_IDS.BODY,
        message: intl.formatMessage({
          id: 'predict.email_pdf_body_required',
          defaultMessage: 'Email body is required'
        })
      };
    }

    hasBeenValidated.current = true;
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [selectedUsers, emailSubject, emailBody, recurrence, recurrenceRepeatValue, recurrenceStartDateError, intl]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  return {
    validationErrors,
    validate,
    revalidate,
    FIELD_IDS
  };
};

export type ScheduleValidation = ReturnType<typeof useScheduleValidation>;
